import type { ArtifactId, BlockId, ChildId, ConversationId, SkillId } from '@aurora/core'
import { auroraStore } from '../store/auroraStore'
import type { UiState } from '../store/auroraStore'

/**
 * The store is the router; the URL hash is a projection of it.
 *
 * There is no route table and no router library. State changes write the hash;
 * a hash change (back button, pasted link) writes the state. Both directions
 * guard against loops by comparing the serialized form before applying.
 */

const VIEWS: UiState['view'][] = ['timeline', 'thread', 'today', 'story', 'playroom', 'game', 'library', 'child', 'kid']

/** Views that became the Playroom: an old link or bookmark lands there. */
const MOVED: Record<string, UiState['view']> = { studio: 'playroom', cards: 'playroom' }

export interface RouteShape {
  view: UiState['view']
  childId?: ChildId
  date?: string
  blockId?: BlockId
  skillId?: SkillId
  artifactId?: ArtifactId
  conversationId?: ConversationId
}

export function serializeRoute(r: RouteShape): string {
  const parts: string[] = [r.view]
  const q = new URLSearchParams()
  if (r.childId) q.set('c', r.childId)
  if (r.date) q.set('d', r.date)
  if (r.blockId) q.set('b', r.blockId)
  if (r.skillId) q.set('s', r.skillId)
  if (r.artifactId) q.set('a', r.artifactId)
  if (r.conversationId) q.set('k', r.conversationId)
  const qs = q.toString()
  return `#/${parts.join('/')}${qs ? `?${qs}` : ''}`
}

export function parseRoute(hash: string): RouteShape {
  const raw = hash.replace(/^#\/?/, '')
  const [path, search] = raw.split('?')
  const seg = (path ?? '').split('/').filter(Boolean)
  const view = (VIEWS as string[]).includes(seg[0] ?? '')
    ? (seg[0] as UiState['view'])
    : MOVED[seg[0] ?? ''] ?? 'thread'
  const q = new URLSearchParams(search ?? '')
  return {
    view,
    childId: (q.get('c') as ChildId) ?? undefined,
    date: q.get('d') ?? undefined,
    blockId: (q.get('b') as BlockId) ?? undefined,
    skillId: (q.get('s') as SkillId) ?? undefined,
    artifactId: (q.get('a') as ArtifactId) ?? undefined,
    conversationId: (q.get('k') as ConversationId) ?? undefined,
  }
}

function routeFromState(s: any): RouteShape {
  return {
    view: s.ui.view,
    childId: s.ui.activeChildId,
    date: s.ui.selectedDate,
    blockId: s.ui.selectedBlockId,
    skillId: s.ui.selectedSkillId,
    artifactId: s.ui.selectedArtifactId,
    conversationId: s.ui.activeConversationId,
  }
}

/** Wire both directions. Returns a teardown. */
export function startHashRouter(): () => void {
  let applying = false

  const applyHashToStore = () => {
    if (applying) return
    const route = parseRoute(window.location.hash)
    const s = auroraStore.getState() as any
    if (serializeRoute(routeFromState(s)) === serializeRoute(route)) return canonicalize()

    applying = true
    try {
      if (route.childId && route.childId !== s.ui.activeChildId) s.setActiveChild(route.childId)
      if (route.view !== s.ui.view) s.setView(route.view)
      if (route.date !== s.ui.selectedDate) s.selectDate(route.date)
      if (route.blockId !== s.ui.selectedBlockId) s.selectBlock(route.blockId)
      if (route.skillId !== s.ui.selectedSkillId) s.selectSkill(route.skillId)
      // A game's page is its artifact; a pasted link to one must open it.
      if (route.artifactId && route.artifactId !== s.ui.selectedArtifactId) s.selectArtifact(route.artifactId)
      if (route.conversationId !== s.ui.activeConversationId) {
        s.selectConversation(route.conversationId)
      }
    } finally {
      applying = false
    }
    canonicalize()
  }

  // An old link (#/studio, #/cards) reads as where it landed.
  const canonicalize = () => {
    const canonical = serializeRoute(routeFromState(auroraStore.getState()))
    if (canonical !== window.location.hash) window.history.replaceState(null, '', canonical)
  }

  const applyStoreToHash = () => {
    if (applying) return
    const next = serializeRoute(routeFromState(auroraStore.getState()))
    if (next === window.location.hash) return
    applying = true
    try {
      // replaceState for incidental selection changes keeps the back button
      // meaningful: it steps between views, not between every tap.
      window.history.replaceState(null, '', next)
    } finally {
      applying = false
    }
  }

  window.addEventListener('hashchange', applyHashToStore)
  const unsub = auroraStore.subscribe(applyStoreToHash)

  if (window.location.hash) applyHashToStore()
  else applyStoreToHash()

  return () => {
    window.removeEventListener('hashchange', applyHashToStore)
    unsub()
  }
}

/** Push a real history entry — used for view switches so back works. */
export function navigate(patch: Partial<RouteShape>) {
  const s = auroraStore.getState() as any
  const next = serializeRoute({ ...routeFromState(s), ...patch })
  window.history.pushState(null, '', next)
  window.dispatchEvent(new HashChangeEvent('hashchange'))
}
